---
layout: docs
title: "What is Corral?"
---


Corral is a complete AI platform that deploys into your own Azure tenant. Your team gets AI assistants, agent building tools, IT governance, and enterprise auth — all running on your infrastructure, behind your network, billed to your Azure subscription.

You deploy from Azure Marketplace. Corral provisions everything — compute, storage, database, AI models, identity, observability — into a managed resource group in your subscription. You can see every resource in your Azure portal. We maintain the software. You own and control the data.

---

## What "Complete" Means

Enterprise AI has a layered dependency problem. Each layer depends on every layer beneath it being solved:

**Models** — LLM providers connected and configured. Corral ships with Azure OpenAI via AI Foundry, with model-agnostic design that supports swapping providers.

**Orchestration** — How agents reason, route between tools, and compose with other agents. Corral's agent framework supports multi-agent composition, context injection, and configurable intelligence types.

**Tools & Data** — How agents connect to the world. Built-in tools for file management, code execution, Microsoft Graph, and rich content rendering. Extensible via MCP and OpenAPI for connecting to any system.

**Guardrails & Safety** — What controls agent behavior. The Cumulative Restrictions Framework tracks what an agent has been exposed to and restricts its outbound actions accordingly. When an agent reads untrusted content, subsequent actions require human review.

**User Surfaces** — How people interact with AI. The Hub for end-users, an Admin Console for operators, an embeddable widget for customer-facing sites, Teams integration, and a full API for custom surfaces.

**Enterprise Controls** — How IT governs everything. Permission scoping per workspace, project, and app. Publish flows with version tracking. Analytics on every conversation, tool call, and model invocation.

**Deployment & Infrastructure** — Where it runs and how it's maintained. On-tenant deployment via Azure Marketplace. Managed updates. Zero data egress by architecture.

Skip any single layer and the whole thing doesn't ship safely. That's why Corral exists as a complete platform — not a point solution for any one layer.

---

## How It's Different from SaaS

| | SaaS AI Tools | Corral |
|---|---|---|
| Where data lives | Vendor's infrastructure | Your Azure subscription |
| Who pays for compute | Vendor (baked into subscription) | You pay Azure directly for your resources |
| Who can access data | Vendor (by policy) | You own it; Corral's access is architecturally constrained |
| Network boundary | Vendor's network | Your network |
| Compliance posture | "Trust us" | "Verify yourself — it's your tenant" |

## How It's Different from Self-Hosted

Unlike traditional self-hosted software where you take full ownership after installation, Corral retains a maintenance relationship. Updates are pushed by Corral. You don't need to manage container deployments, database migrations, or infrastructure changes. You get the data-residency benefits of self-hosted with the maintenance benefits of SaaS.

---

## What You Pay

Two separate charges:

1. **Platform subscription** — Corral's fee for the software and ongoing maintenance. This appears as a line item on your Azure Marketplace bill.

2. **Azure resource consumption** — The infrastructure Corral deploys (compute, storage, database, AI services, networking) runs on your Azure subscription. Azure bills you directly for these resources, just like any other Azure resource you own. Corral includes built-in analytics to monitor and allocate token consumption — useful for understanding usage patterns or handling internal and departmental billing.

Both charges can count against your existing MACC (Microsoft Azure Consumption Commitment) spend.

---

## Who It's For

**Product teams and ops managers** who need AI deployed without a six-month procurement cycle or a seven-figure SI contract.

**Developers and engineers** who've been building the AI stack internally and want their time back to focus on business-specific logic instead of auth, governance, and deployment infrastructure.

**IT and security teams** who want full visibility and control over agents and their actions, data flow, and guardrails.

**Service providers and SIs** who need a platform they can extend and deploy into client environments without reinventing the wheel.
